import { observable, makeObservable, action } from "mobx";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { INVOICE_PROPERTY, INVOICE_STATE } from "./InvoiceEnums";

import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import Axios from "src/network/Axios";

export enum RESTART_CANCEL_INVOICE_STATE {
    INIT,
    RESTARTING,
    CANCELLING,
    FINISHED,
    SUCCESS,
    ERROR
}

export enum RESTART_CANCEL_MODAL_TYPE {
    RESTART,
    CANCEL,
    DELETE
}

export class RestartCancelInvoiceStore {
    rootStore: RootStore;

    isModalDisplayed = false;
    displayedModalType: RESTART_CANCEL_MODAL_TYPE = RESTART_CANCEL_MODAL_TYPE.RESTART;

    restartCancelInvoiceState: RESTART_CANCEL_INVOICE_STATE = RESTART_CANCEL_INVOICE_STATE.INIT;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            restartCancelInvoiceState: observable,
            isModalDisplayed: observable,
            displayedModalType: observable,

            reset: action,
            setIsModalDisplayed: action,
            setRestartCancelInvoiceState: action,
            restartInvoice: action,
            cancelInvoice: action,
            setDisplayedModalType: action
        });
    }

    reset = () => {
        this.restartCancelInvoiceState = RESTART_CANCEL_INVOICE_STATE.INIT;
        this.isModalDisplayed = false;
        this.displayedModalType = RESTART_CANCEL_MODAL_TYPE.RESTART;
    };

    cancelInvoice = async (invoiceId: string, workflowId: string, deleteInvoice: boolean): Promise<boolean> => {
        this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.CANCELLING);

        let wasInvoiceSuccessfullyCanceled = false;

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        const cancelInvoiceVariables = {
            invoiceid: invoiceId,
            workflowid: workflowId,
            cancelled: true,
            delete: deleteInvoice
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.cancelInvoiceUrl, cancelInvoiceVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            wasInvoiceSuccessfullyCanceled = true;
            this.rootStore.invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.STATE, INVOICE_STATE.CANCELLED);
        } catch (error) {
            console.error("Error cancelling invoice: ", error);
        }

        if (wasInvoiceSuccessfullyCanceled) {
            if (deleteInvoice) {
                this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.INIT);
            } else {
                this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.FINISHED);
            }
        } else {
            this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.INIT);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.action.cancel_invoice.failure"),
                MessageType.ERROR
            );
        }
        return wasInvoiceSuccessfullyCanceled;
    };

    restartInvoice = async (): Promise<boolean> => {
        this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.RESTARTING);

        let wasInvoiceSuccessfullyRestarted = false;

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        const concludeProcessCancelTaskVariables = {
            taskname: "ProcessCancel",
            workflowid: currentInvoice?.workflowInstance
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.concludeHumanTaskUrl, concludeProcessCancelTaskVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            wasInvoiceSuccessfullyRestarted = true;
            this.rootStore.invoiceStore.currentInvoice?.updateProperty(
                INVOICE_PROPERTY.STATE,
                INVOICE_STATE.READY_FOR_VISA
            );
        } catch (error) {
            console.error("Error restarting invoice: ", error);
        }

        if (wasInvoiceSuccessfullyRestarted) {
            this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.FINISHED);
        } else {
            this.setRestartCancelInvoiceState(RESTART_CANCEL_INVOICE_STATE.INIT);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.action.restart_invoice.failure"),
                MessageType.ERROR
            );
        }
        return wasInvoiceSuccessfullyRestarted;
    };

    /* SETTERS */
    setIsModalDisplayed = (isModalDisplayed: boolean) => {
        this.isModalDisplayed = isModalDisplayed;
    };

    setRestartCancelInvoiceState = (assignInvoiceState: RESTART_CANCEL_INVOICE_STATE) => {
        this.restartCancelInvoiceState = assignInvoiceState;
    };

    setDisplayedModalType = (displayedModalType: RESTART_CANCEL_MODAL_TYPE) => {
        this.displayedModalType = displayedModalType;
    };
}
